import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loading } from "@/components/bi/Loading";
import { Gift, Plus, Trash2, Loader2, LayoutDashboard, Database, Rocket, Check, AlertTriangle } from "lucide-react";

const LEVELS = [["credentials", "Hide credentials"], ["definition", "Hide credentials + query"], ["hidden", "Hide the source completely"]];
const MODES = [["copy", "Copy"], ["link", "Live link"]];

// Welcome pack: what every brand-new tenant opens on.
export function WelcomePack({ tenants = [] }) {
  const [pack, setPack] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [options, setOptions] = useState({ datasets: [], dashboards: [] });
  const [draft, setDraft] = useState({ kind: "dashboard", id: "", mode: "copy", visibility_level: "credentials" });
  const [picked, setPicked] = useState([]);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);

  const load = useCallback(() => {
    api.get("/managed-sources/welcome-pack").then(({ data }) => setPack(data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Only Eazy staff can curate the welcome pack"); setPack({ items: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!adding) return;
    Promise.all([api.get("/datasets"), api.get("/dashboards")])
      .then(([ds, db]) => setOptions({ datasets: ds.data || [], dashboards: db.data || [] }))
      .catch(() => setOptions({ datasets: [], dashboards: [] }));
  }, [adding]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/managed-sources/welcome-pack", {
        enabled: next.enabled,
        items: (next.items || []).map(({ kind, id, mode, visibility_level }) => ({ kind, id, mode, visibility_level })),
      });
      setPack(data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the welcome pack");
      return false;
    } finally { setSaving(false); }
  };

  const addItem = async () => {
    if (!draft.id) { toast.error("Pick something to add"); return; }
    if ((pack.items || []).some((i) => i.id === draft.id)) { toast.error("That is already in the pack"); return; }
    if (await save({ enabled: pack.enabled, items: [...(pack.items || []), draft] })) {
      setAdding(false); setDraft({ ...draft, id: "" });
      toast.success("Added to the welcome pack");
    }
  };

  const removeItem = async (id) => {
    if (await save({ enabled: pack.enabled, items: (pack.items || []).filter((i) => i.id !== id) })) {
      toast.success("Removed");
    }
  };

  const applyNow = async () => {
    if (!picked.length) { toast.error("Pick at least one tenant"); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/managed-sources/welcome-pack/apply", { tenant_ids: picked });
      setResults(data.tenants || []);
      toast.success(`Applied to ${(data.tenants || []).filter((t) => t.ok).length} tenant(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply the pack"); }
    finally { setApplying(false); }
  };

  if (!pack) return <Loading label="Loading the welcome pack" />;
  const items = pack.items || [];

  return (
    <div className="space-y-4" data-testid="welcome-pack">
      <div className="glass-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-[240px] flex-1">
            <h2 className="font-display text-base md:text-lg text-[color:var(--ink)] flex items-center gap-2">
              <Gift className="w-4 h-4 text-[color:var(--brand-ocean)]" />Welcome pack
            </h2>
            <p className="text-xs text-[color:var(--ink-3)] mt-1 max-w-xl">
              Every brand-new tenant opens on these, so a new client never lands on an empty screen.
              Copies are theirs to edit; live links stay yours and keep updating.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--ink-3)]" />}
            <span className="text-xs text-[color:var(--ink-2)]">{pack.enabled ? "On for new tenants" : "Off"}</span>
            <Switch data-testid="pack-enabled" checked={!!pack.enabled} disabled={saving}
              onCheckedChange={(v) => save({ enabled: v, items })} />
          </div>
        </div>

        <div className="mt-4 space-y-1.5" data-testid="pack-items">
          {items.map((i) => (
            <div key={i.id} data-testid={`pack-item-${i.id}`}
              className="flex items-center gap-3 rounded-lg border border-[color:var(--glass-border)] px-3 py-2">
              {i.kind === "dashboard"
                ? <LayoutDashboard className="w-3.5 h-3.5 text-[color:var(--brand-ocean)] shrink-0" />
                : <Database className="w-3.5 h-3.5 text-[color:var(--brand-mint)] shrink-0" />}
              <span className="text-xs text-[color:var(--ink)] flex-1 truncate">
                {i.name || i.id}
                {i.missing && <span className="text-[color:var(--danger)] ml-1.5">(deleted)</span>}
              </span>
              <Badge variant="outline" className="text-[9px]">{i.mode === "link" ? "live link" : "copy"}</Badge>
              <Badge variant="outline" className="text-[9px]">{(LEVELS.find((l) => l[0] === i.visibility_level) || LEVELS[0])[1]}</Badge>
              <button data-testid={`pack-remove-${i.id}`} onClick={() => removeItem(i.id)}
                className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {!items.length && <p data-testid="pack-empty" className="text-xs text-[color:var(--ink-3)]">The shelf is empty — add a dashboard or a data source below.</p>}
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Button size="sm" variant="outline" data-testid="pack-add-btn" onClick={() => setAdding(true)} className="bg-[color:var(--surface)]">
            <Plus className="w-3.5 h-3.5 mr-1" />Add to pack
          </Button>
          <Button size="sm" data-testid="pack-apply-btn" disabled={!items.length}
            onClick={() => { setPicked([]); setResults(null); setApplyOpen(true); }}
            className="bg-[color:var(--brand-ocean)] text-white">
            <Rocket className="w-3.5 h-3.5 mr-1" />Apply to existing tenants…
          </Button>
          <span className="text-[10px] text-[color:var(--ink-3)]">
            {pack.updated_by ? `Curated by ${pack.updated_by}` : "Not curated yet"}
            {pack.applied_count ? ` · delivered ${pack.applied_count} time(s)` : ""}
          </span>
        </div>
      </div>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="pack-add-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Add to the welcome pack</DialogTitle>
            <DialogDescription>Anything in your own Eazy workspace can travel to new tenants.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v, id: "" })}>
              <SelectTrigger data-testid="pack-kind" className="bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dashboard">Dashboard</SelectItem>
                <SelectItem value="dataset">Data source</SelectItem>
              </SelectContent>
            </Select>
            <Select value={draft.id} onValueChange={(v) => setDraft({ ...draft, id: v })}>
              <SelectTrigger data-testid="pack-item-select" className="bg-[color:var(--surface)]"><SelectValue placeholder={`Pick a ${draft.kind}`} /></SelectTrigger>
              <SelectContent>
                {(draft.kind === "dashboard" ? options.dashboards : options.datasets).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={draft.mode} onValueChange={(v) => setDraft({ ...draft, mode: v })}>
              <SelectTrigger data-testid="pack-mode" className="bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={draft.visibility_level} onValueChange={(v) => setDraft({ ...draft, visibility_level: v })}>
              <SelectTrigger data-testid="pack-level" className="bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>{LEVELS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button data-testid="pack-add-confirm" onClick={addItem} disabled={saving || !draft.id} className="bg-[color:var(--brand-ocean)] text-white">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent data-testid="pack-apply-dialog" className="max-w-md max-h-[86vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Apply the pack now</DialogTitle>
            <DialogDescription>Deliver the shelf to tenants that already exist. Items land again if you repeat this.</DialogDescription>
          </DialogHeader>
          {results ? (
            <div className="space-y-1.5 py-1" data-testid="pack-apply-results">
              {results.map((r) => (
                <div key={r.tenant_id} className="flex items-center gap-2 rounded-lg border border-[color:var(--glass-border)] px-3 py-2 text-xs">
                  {r.ok ? <Check className="w-3.5 h-3.5 text-[color:var(--success)]" /> : <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--danger)]" />}
                  <span className="flex-1 truncate text-[color:var(--ink)]">{r.tenant_name || r.tenant_id}</span>
                  <span className="text-[color:var(--ink-3)]">{r.ok ? `${r.applied} item(s)` : r.error}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-h-[260px] overflow-y-auto rounded-lg border border-[color:var(--glass-border)] divide-y divide-[color:var(--glass-border)] my-1" data-testid="pack-apply-tenants">
              {tenants.map((t) => (
                <label key={t.id} data-testid={`pack-apply-tenant-${t.id}`} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[color:var(--surface-2)]">
                  <Checkbox checked={picked.includes(t.id)}
                    onCheckedChange={() => setPicked((p) => (p.includes(t.id) ? p.filter((x) => x !== t.id) : [...p, t.id]))} />
                  <span className="text-xs text-[color:var(--ink)] flex-1 truncate">{t.name}</span>
                </label>
              ))}
              {!tenants.length && <p className="px-3 py-4 text-[11px] text-[color:var(--ink-3)]">No tenants yet.</p>}
            </div>
          )}
          <DialogFooter>
            {results ? <Button data-testid="pack-apply-close" onClick={() => setApplyOpen(false)} className="bg-[color:var(--brand-ocean)] text-white">Done</Button> : (
              <>
                <Button variant="ghost" onClick={() => setApplyOpen(false)}>Cancel</Button>
                <Button data-testid="pack-apply-confirm" onClick={applyNow} disabled={applying || !picked.length} className="bg-[color:var(--brand-ocean)] text-white">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Deliver to ${picked.length || ""} tenant${picked.length === 1 ? "" : "s"}`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
